/**
 * video-audio-lane.js — window.VideoAudioLane
 *
 * Re-encode ONLY the soundtrack of an mp4-family video, with the browser's own
 * codecs, and hand the picture through untouched.
 *
 *     decoded PCM ──▶ process() ──▶ [fast limiter] ──▶ AudioEncoder (AAC)
 *          ──▶ mp4-muxer (.m4a in memory) ──▶ MP4TrackRemux.replaceAudio
 *          (the video's bytes as file.slice() references; nothing copied)
 *
 * WHY (2026-09-12, /video-volume report #2371, user #2371, Android 16 /
 * Yandex 26.8). A volume change on a 43 MB, 3-minute clip took 62 s in
 * single-thread FFmpeg-WASM on that phone (`-c:v copy -af … -c:a aac`); the
 * reporter cancelled at ~60 s three times in a row, then filed «зависает».
 * The picture is not touched by that command at all — the whole cost is the
 * AAC decode + filter + AAC encode in wasm, plus shovelling the 43 MB through
 * the wasm heap twice. RU shard, 60 days: streamed runs of this tool average
 * 78 s on desktop and 170 s on phones; 145 of 623 sessions that pressed
 * «Apply» never downloaded.
 *
 * Measured at 4× CPU throttle on a 175 s / 44 MB h264+aac clip in real Chrome
 * (tests/scratch/vv-lanes-scratch.mjs, 2026-09-12):
 *   FFmpeg lane, whole run ………………………… 20.9 s   (copy-only floor 3.2 s)
 *   `-aac_coder fast` ………………………………… 19.9 s   (no help — the coder is not the cost)
 *   native: mastering true-peak limiter ……  8–10 s  (even at 0 dB reduction → not usable here)
 *   native: gain pass ………………………………… 0.4 s
 *   native: AudioEncoder AAC-LC ……………… 4.9 s
 *   native: FFmpeg two-input `-c copy` ……… 3.1 s   (scales with the file, 2 GiB wall)
 *   native: MP4TrackRemux (zero-copy) ……… 2.1 s   (part count, not file size)
 * So the lane is: gain + createFastLimiter (the alimiter class, ~0.4 s in
 * node for the same clip) + native AAC + zero-copy remux.
 *
 * Whole run through the tool on that clip: 4× — 6.2 s against 20.9; 12×
 * (the reporter's phone class, where FFmpeg needed 62 s) — 19.3 s. Note that
 * 12× throttles the MAIN THREAD only: the platform encoder is not slowed, so
 * what remains on the phone is main-thread JS — the block loop (copy 0.1 s,
 * gain 0.8 s, AudioData 0.5 s, encode() 3.0 s) and, in the gaps between
 * blocks, the encoder's ~8 200 output callbacks (one AAC access unit each:
 * the AacAccessUnit door + mp4-muxer bookkeeping) — see `steps` in the
 * vv_lane event. The first build did this in 49 s: a 4 s block loop, an
 * interleave loop per block, and a 2000-part remux (11.8 s in one task).
 * Output verified against the source: video packets md5-identical, audio lag
 * +0.9 / +2.0 ms (cross-correlation), LUFS exact, true peak −1.5 dBTP with
 * the limiter (the same sample ceiling alimiter had).
 *
 * FALLBACKS, all named in the result/throw so the caller's FFmpeg lane can
 * take over and telemetry can count them:
 *   • no AudioEncoder / AAC unsupported at this rate+channels (Firefox, Linux
 *     Chrome, a codec the platform lacks) → throws {laneReason}
 *   • the encoder refuses the sample rate → the PCM is resampled through an
 *     OfflineAudioContext to 48 kHz / 44.1 kHz before encoding (Windows Media
 *     Foundation accepts only those two — measured: a 24 kHz mono source is
 *     refused with «Unsupported sample rate. Supported values: 44100, 48000»);
 *   • the encoder refuses the bitrate → nearest value from the list it prints,
 *     else 128 kb/s (MF: 96/128/160/192 only);
 *   • the remux refuses the container (fragmented, edit list, external media…)
 *     → FFmpeg two-input `-c copy` when FFmpegProcessor is on the page, else
 *     throws {laneReason: 'remux:<reason>'}.
 *
 * Every AudioEncoder here is built through AacAccessUnit.encoder — the one
 * door for AAC on its way into an MP4 (Chromium 109–111 ADTS framing, report
 * #6990). Dependencies (mp4-muxer, aac-access-unit, mp4-track-remux) are
 * loaded on demand through __jsSrc; call warm() at file pick so the click
 * pays nothing.
 *
 *   var can = await VideoAudioLane.probe({ file, outExt, sampleRate, channels, bitrateKbps });
 *   if (can.ok) {
 *       var res = await VideoAudioLane.run({
 *           file, buffer,                    // File + decoded AudioBuffer
 *           startSec: undefined,             // where buffer frame 0 sits on the movie's clock:
 *                                            //   omitted = the video's own sound (MP4TrackRemux.soundStart),
 *                                            //   0 = already on the movie's clock (a mix, generated sound)
 *           process: function (chans, startFrame) { … in place … },   // optional; startFrame on the MOVIE's clock
 *           limiter: { ceilingDb: -1, windowMs: 5 },                     // optional
 *           bitrateKbps: 128, slug: 'video-volume',
 *           maxFrames: 0,                    // 0 = весь буфер; иначе обрезать, как `-t`
 *           onProgress: function (ratio, phase) {},                     // 0..1
 *           signal: abortController.signal
 *       });
 *       res.blob; res.ms; res.remux ('zero-copy' | 'ffmpeg'); res.encoder
 *   }
 */
(function (w) {
    'use strict';
    if (w.VideoAudioLane) return;

    var MP4_EXT = /^(mp4|m4v|mov|3gp|3g2)$/i;
    var ENC_RATES = [48000, 44100];
    /* Block size and remux part budget. Both are main-thread costs and both
       were measured at 12× CPU throttle on the 175 s clip (the reporter's
       phone class): a 4 s block loop of 44 iterations spent ~35 s, and the
       zero-copy remux with the default 2000-part budget was one 11.8 s
       synchronous task (1803 parts of file.slice()). Overridable for the
       stand via window.__videoAudioLaneTune = { blockSec, remuxMaxParts }. */
    function tune(k, d) { var t = w.__videoAudioLaneTune; return (t && t[k] > 0) ? +t[k] : d; }
    var BLOCK_SEC = 10;
    var REMUX_MAX_PARTS = 64;

    function laneErr(reason, msg) { var e = new Error(msg || reason); e.laneReason = reason; return e; }
    function src(p) { return (typeof w.__jsSrc === 'function') ? w.__jsSrc(p) : p; }
    function loadScript(url) {
        return new Promise(function (res, rej) {
            var s = document.createElement('script');
            s.src = url; s.async = true;
            s.onload = function () { res(); };
            s.onerror = function () { rej(new Error('script-load-failed: ' + url)); };
            document.head.appendChild(s);
        });
    }
    var _deps = null;
    function ensureDeps() {
        if (_deps) return _deps;
        var need = [];
        if (!w.Mp4Muxer) need.push(loadScript(src('/vendor/mp4-muxer/mp4-muxer.js')));
        if (!w.AacAccessUnit) need.push(loadScript(src('/js/aac-access-unit.js')));
        if (!w.MP4TrackRemux) need.push(loadScript(src('/js/mp4-track-remux.js')));
        if (!w.MasteringLimiter) need.push(loadScript(src('/js/mastering-limiter.js')));
        _deps = Promise.all(need).then(function () {
            if (!w.Mp4Muxer || !w.AacAccessUnit || !w.MP4TrackRemux || !w.MasteringLimiter) throw laneErr('deps-missing');
        });
        _deps.catch(function () { _deps = null; });
        return _deps;
    }

    async function supported(cfg) {
        try { var r = await AudioEncoder.isConfigSupported(cfg); return !!(r && r.supported); } catch (_) { return false; }
    }

    /* Which (sampleRate, bitrate) this browser will encode AAC-LC at for the
       given source. Returns { ok, sampleRate, bitrate, resample } or { ok:false, reason }. */
    async function pickEncoderConfig(sampleRate, channels, bitrateKbps) {
        if (typeof w.AudioEncoder !== 'function' || typeof w.AudioData !== 'function') return { ok: false, reason: 'no-audio-encoder' };
        var kbps = Math.max(32, Math.min(320, Math.round(bitrateKbps || 128)));
        var rates = [sampleRate].concat(ENC_RATES.filter(function (r) { return r !== sampleRate; }));
        for (var i = 0; i < rates.length; i++) {
            var cfg = { codec: 'mp4a.40.2', sampleRate: rates[i], numberOfChannels: channels, bitrate: kbps * 1000 };
            if (await supported(cfg)) return { ok: true, sampleRate: rates[i], bitrate: kbps * 1000, resample: rates[i] !== sampleRate };
            // some platforms answer "unsupported" for the bitrate alone
            var cfg2 = { codec: 'mp4a.40.2', sampleRate: rates[i], numberOfChannels: channels, bitrate: 128000 };
            if (await supported(cfg2)) return { ok: true, sampleRate: rates[i], bitrate: 128000, resample: rates[i] !== sampleRate };
        }
        return { ok: false, reason: 'aac-unsupported:' + sampleRate + 'x' + channels };
    }

    /* Can this file + output go through the lane at all? Cheap, no decode. */
    async function probe(o) {
        o = o || {};
        var ext = String(o.outExt || '').toLowerCase();
        if (!MP4_EXT.test(ext)) return { ok: false, reason: 'out-ext:' + ext };
        if (!o.file || !(o.file instanceof Blob)) return { ok: false, reason: 'no-file' };
        var inExt = String((o.file.name || '').split('.').pop() || '').toLowerCase();
        if (!MP4_EXT.test(inExt) && !/mp4|quicktime/.test(o.file.type || '')) return { ok: false, reason: 'in-ext:' + inExt };
        var enc = await pickEncoderConfig(o.sampleRate || 48000, o.channels || 2, o.bitrateKbps);
        if (!enc.ok) return enc;
        return { ok: true, encoder: enc };
    }

    /* Resample a whole AudioBuffer with the browser's own resampler. */
    async function resample(buffer, rate) {
        var Off = w.OfflineAudioContext || w.webkitOfflineAudioContext;
        var frames = Math.ceil(buffer.duration * rate);
        var oc = new Off(buffer.numberOfChannels, frames, rate);
        var s = oc.createBufferSource(); s.buffer = buffer; s.connect(oc.destination); s.start();
        return await oc.startRendering();
    }

    /* Parse «Unsupported bitrate. Supported values: 96000, 128000, …» → nearest. */
    function nearestFromError(err, want) {
        var m = /Supported values:\s*([\d,\s]+)/.exec(String(err && err.message || ''));
        if (!m) return 0;
        var vals = m[1].split(',').map(function (x) { return parseInt(x, 10); }).filter(function (x) { return x > 0; });
        if (!vals.length) return 0;
        vals.sort(function (a, b) { return Math.abs(a - want) - Math.abs(b - want); });
        return vals[0];
    }

    function nowMs() { return (w.performance && performance.now) ? performance.now() : Date.now(); }
    /* A real macrotask, deliberately NOT scheduler.yield(): yield()
       continuations are prioritised over ordinary tasks, so a loop that waits
       for the encoder's output callbacks by yielding starves the very tasks
       that would let it stop. Measured 2026-09-12 on a 60 s clip: the queue
       reached 9, the loop spun forever, Debugger.pause landed in feed() every
       time — a page frozen solid with no error anywhere. */
    function yieldMacro() { return new Promise(function (r) { setTimeout(r, 0); }); }
    /* Backpressure: wake on the encoder's own 'dequeue', with a floor so a
       platform that never fires it still makes progress. */
    function dequeueWait(encoder) {
        return new Promise(function (r) {
            var done = false;
            function fin() { if (done) return; done = true; try { encoder.removeEventListener('dequeue', fin); } catch (_) {} r(); }
            try { encoder.addEventListener('dequeue', fin); } catch (_) {}
            setTimeout(fin, 100);
        });
    }

    /* ── Segment-parallel AAC (16.09.2026) ────────────────────────────────
       The platform AAC encoder is one instance on one thread, and on Android
       it is slow: field, 13–15.09, two sessions with 44–57 MB clips spent
       43–48 s in the encode phase, 39 s of it inside encoder.flush() — the
       block loop had long finished, the encoder had not. Windows Media
       Foundation is ~90× real time, so a desktop cannot show the gain; a
       phone's MediaCodec AAC runs ~10–15× and has other cores idle.

       So a clip over two minutes is cut into segments of ≥60 s, each with
       its own encoder AND its own limiter, run concurrently; every segment
       encodes a lead-in and tail of OV frames beyond its range (limiter
       release ×5, encoder warm-up), and only the frames that START inside
       the range are kept, re-based to the file clock. Frame boundaries line
       up because segment bounds and OV are multiples of the AAC frame.

       The join was measured, not assumed (tests/scratch/aac-parallel-scratch
       .mjs + aac-splice-check-scratch.py, real Chrome): on a 175 s clip the
       error against the source in ±100 ms around the three joins was
       −80.5 / −82.6 / −85.1 dB against −80.6 / −81.7 / −81.8 dB for a single
       encoder in the same windows; on 60 s of speech −66.7 vs −66.7 dB at
       every join; the two encodes differ by −90.5 / −82.9 dBFS overall, peak
       sample difference 0.0004. The platform-speed premise (MediaCodec
       scales across instances) is what the field must confirm: `par` and
       `flush` ride in the vv_lane event, `dur` gives the throughput. */
    var OV_FRAMES = 12 * 1024;
    function parallelSegments(audioSec) {
        var forced = tune('par', 0);
        if (forced) return forced;
        var cores = (w.navigator && w.navigator.hardwareConcurrency) || 2;
        if (audioSec < 120 || cores < 4) return 1;
        return Math.max(1, Math.min(4, cores - 1, Math.floor(audioSec / 60)));
    }
    async function encodeSegment(job) {
        var rangeFrom = job.from - job.lead, rangeTo = job.to + job.tail;
        var out = [], meta0 = null, encErr = null, fed = 0, c, k;
        var enc = w.AacAccessUnit.encoder({
            output: function (ch, m) {
                if (m && m.decoderConfig && !meta0) meta0 = m;
                var d = new Uint8Array(ch.byteLength); ch.copyTo(d);
                out.push({ ts: ch.timestamp, dur: ch.duration, data: d });
            },
            error: function (e) { encErr = encErr || e; }
        }, { slug: job.slug });
        enc.configure({ codec: 'mp4a.40.2', sampleRate: job.sampleRate, numberOfChannels: job.nCh, bitrate: job.bitrate });
        var lim = job.limiter ? w.MasteringLimiter.createFastLimiter(job.sr, job.nCh, job.limiter) : null;
        var planar = new Float32Array(job.block * job.nCh), packed = new Float32Array(job.block * job.nCh);
        async function push(buf, n) {
            var ad = new w.AudioData({ format: 'f32-planar', sampleRate: job.sr, numberOfChannels: job.nCh, numberOfFrames: n,
                timestamp: Math.round(fed / job.sr * 1e6), data: buf.subarray(0, n * job.nCh) });
            try { enc.encode(ad); } finally { ad.close(); }
            fed += n;
            if (encErr) throw encErr;
            while (enc.encodeQueueSize > 8 && !encErr) await dequeueWait(enc);
        }
        async function pushBlocks(blocks) {
            for (var b = 0; b < blocks.length; b++) {
                var ch = blocks[b], n = ch[0].length;
                if (!n) continue;
                if (packed.length < n * job.nCh) packed = new Float32Array(n * job.nCh);
                for (k = 0; k < job.nCh; k++) packed.set(ch[k], k * n);
                await push(packed, n);
            }
        }
        var gr = 0;
        try {
            for (var off = rangeFrom; off < rangeTo; off += job.block) {
                job.checkAbort();
                var n = Math.min(job.block, rangeTo - off), views = [];
                for (c = 0; c < job.nCh; c++) views.push(planar.subarray(c * n, (c + 1) * n));
                job.fill(views, off, n);
                if (job.process) job.process(views, off);
                if (lim) await pushBlocks(lim.push(views, n)); else await push(planar, n);
                job.onFrames(n);
                await yieldMacro();
            }
            if (lim) { var tail = lim.flush(); gr = tail.maxGrDb; await pushBlocks(tail.blocks); }
            job.checkAbort();
            await enc.flush();
            if (encErr) throw encErr;
        } finally { try { enc.close(); } catch (_) {} }
        var keep = [];
        for (var i = 0; i < out.length; i++) {
            var startFrame = Math.round(out[i].ts / 1e6 * job.sr) + rangeFrom;
            if (startFrame >= job.from && startFrame < job.to) keep.push({ ts: Math.round(startFrame / job.sr * 1e6), dur: out[i].dur, data: out[i].data });
        }
        if (!keep.length) throw laneErr('encoder-empty');
        return { chunks: keep, meta: meta0, maxGrDb: gr };
    }

    async function run(o) {
        var t0 = nowMs(), ms = {};
        var signal = o.signal || null;
        var slug = o.slug || (w.__TOOL_SLUG || '');
        var onProgress = typeof o.onProgress === 'function' ? o.onProgress : function () {};
        function aborted() { return !!(signal && signal.aborted); }
        function checkAbort() { if (aborted()) { var e = new Error('Aborted by user'); e.name = 'AbortError'; e.laneReason = 'aborted'; throw e; } }

        await ensureDeps();
        var buffer = o.buffer;
        if (!buffer || !buffer.numberOfChannels) throw laneErr('no-buffer');
        /* Where the buffer's frame 0 belongs on the movie's clock. The buffer
           is the video's OWN sound, decoded, and every decoder starts at the
           track's first presented sample — which the movie plays
           MP4TrackRemux.soundStart() after the picture (measured 01.10.2026:
           a track starting 0.478 s in came back from this lane 478 ms early,
           tests/playwright/decoded-sound-keeps-its-start.mjs). The lead goes into the
           stream as digital silence, not as an edit list: Media Foundation
           ignores an empty edit (SoundtrackSplice). A caller whose buffer is
           already on the movie's clock — a mix that placed the video's sound
           itself, a soundtrack generated for the picture — passes startSec: 0. */
        var startSec = (typeof o.startSec === 'number' && isFinite(o.startSec)) ? o.startSec
            : await w.MP4TrackRemux.soundStart(o.file);
        var nCh = buffer.numberOfChannels, sr = buffer.sampleRate;
        var enc = await pickEncoderConfig(sr, nCh, o.bitrateKbps);
        if (!enc.ok) throw laneErr(enc.reason);

        /* ── 1. PCM: process, (resample), limit, feed the encoder in blocks ── */
        var tA = nowMs();
        var chans = [], c;
        for (c = 0; c < nCh; c++) chans.push(buffer.getChannelData(c));
        /* maxFrames — та же семантика, что `-t` у FFmpeg: «дорожка длиннее
           картинки кодируется только до её конца». Без этой опции вызывающему
           пришлось бы РЕЗАТЬ буфер копией — лишняя аллокация ровно там, где
           память и так узкое место (замер /add-music-to-video 16.09.2026:
           3-минутный стерео-микс это 69 МБ PCM, и на iOS потолок вкладки
           500–700 МБ на всё). */
        var N = chans[0].length;
        if (o.maxFrames > 0 && o.maxFrames < N) N = Math.round(o.maxFrames);
        var lim = o.limiter ? w.MasteringLimiter.createFastLimiter(sr, nCh, o.limiter) : null;
        var blockSec = tune('blockSec', BLOCK_SEC);
        var block = Math.max(1, Math.round(blockSec * sr));

        var muxer = new w.Mp4Muxer.Muxer({
            target: new w.Mp4Muxer.ArrayBufferTarget(),
            audio: { codec: 'aac', sampleRate: enc.sampleRate, numberOfChannels: nCh },
            fastStart: 'in-memory'
        });
        var encErr = null, first = true, chunks = 0, outputMs = 0;
        var encoder = w.AacAccessUnit.encoder({
            output: function (chunk, meta) {
                var t = nowMs();
                try { muxer.addAudioChunk(chunk, first ? meta : undefined); chunks++; first = false; }
                catch (e) { encErr = encErr || e; }
                outputMs += nowMs() - t;
            },
            error: function (e) { encErr = encErr || e; }
        }, { slug: slug });
        var bitrate = enc.bitrate;
        try {
            encoder.configure({ codec: 'mp4a.40.2', sampleRate: enc.sampleRate, numberOfChannels: nCh, bitrate: bitrate });
        } catch (e1) {
            var alt = nearestFromError(e1, bitrate);
            if (!alt) throw laneErr('encoder-configure', e1 && e1.message);
            bitrate = alt;
            encoder.configure({ codec: 'mp4a.40.2', sampleRate: enc.sampleRate, numberOfChannels: nCh, bitrate: bitrate });
        }

        // Resampling is whole-buffer (the browser's resampler is an offline
        // render); it happens BEFORE the block loop so the limiter and the
        // encoder see the rate the file will carry.
        var procSr = sr;
        if (enc.resample) {
            var work = buffer;
            if (o.process) {
                // process() is defined at the source rate (segments in seconds
                // → frames at sr); apply it before resampling, on a copy — its
                // offset on the movie's clock, as in the block loop below.
                var copy = new w.AudioBuffer({ numberOfChannels: nCh, length: N, sampleRate: sr });
                for (c = 0; c < nCh; c++) copy.copyToChannel(chans[c], c);
                var cc = []; for (c = 0; c < nCh; c++) cc.push(copy.getChannelData(c));
                o.process(cc, Math.round(Math.max(0, startSec) * sr));
                work = copy;
            }
            var rs = await resample(work, enc.sampleRate);
            chans = []; for (c = 0; c < nCh; c++) chans.push(rs.getChannelData(c));
            N = chans[0].length; procSr = enc.sampleRate;
            if (o.maxFrames > 0) N = Math.min(N, Math.round(o.maxFrames * enc.sampleRate / sr));
            block = Math.max(1, Math.round(blockSec * procSr));
            if (lim) lim = w.MasteringLimiter.createFastLimiter(procSr, nCh, o.limiter);
        }

        /* The stream the encoder gets is on the MOVIE's clock: L frames of the
           lead's silence, then the buffer (NV frames in all, still capped by
           maxFrames — the picture's length). fillBlock writes frames
           [vOff, vOff+n) of it. process() gets the same movie-clock offset:
           its segments and fades are what the person set against the video's
           own time (/video-volume previews them by video.currentTime, and its
           FFmpeg lane applies them on the stream's PTS), and on the lead's
           silence any gain is still silence. */
        var L = Math.max(0, Math.round(Math.max(0, startSec) * procSr));
        var NV = L + N;
        if (L && o.maxFrames > 0) NV = Math.min(NV, Math.round(o.maxFrames * procSr / sr));
        function fillBlock(views, vOff, n) {
            var k = Math.max(0, Math.min(n, L - vOff));
            for (var c2 = 0; c2 < nCh; c2++) {
                if (k) views[c2].fill(0, 0, k);
                if (k < n) views[c2].set(chans[c2].subarray(vOff + k - L, vOff + n - L), k);
            }
        }

        /* Per-block work is main-thread JS and it is what a slow phone pays
           for: measured at 12× CPU throttle on the 175 s clip, the first shape
           (copy → gain loop → interleave loop → AudioData) spent 29–44 s in
           this loop. So the PCM is handed to the encoder PLANAR — the block
           is one buffer holding channel 0 then channel 1, which is the layout
           the limiter and process() already work in — and the only per-sample
           JS left is the caller's process(). No interleave pass, no scratch
           copy on top of the planar one. */
        var audioSec = NV / procSr, par = parallelSegments(audioSec), maxGrDb = 0, parMs = 0;
        if (par >= 2) {
            try { encoder.close(); } catch (_) {}
            var tP = nowMs(), segLen = Math.ceil(NV / par / 1024) * 1024, jobs = [], doneFrames = 0, totalWork = NV;
            var segProcess = (o.process && !enc.resample) ? o.process : null;
            for (var si = 0; si < par; si++) {
                var sFrom = si * segLen, sTo = Math.min(NV, (si + 1) * segLen);
                if (sFrom >= sTo) break;
                var lead = Math.min(OV_FRAMES, sFrom), tailN = Math.min(OV_FRAMES, NV - sTo);
                totalWork += lead + tailN;
                jobs.push(encodeSegment({ from: sFrom, to: sTo, lead: lead, tail: tailN, fill: fillBlock, sr: procSr, nCh: nCh, bitrate: bitrate,
                    sampleRate: enc.sampleRate, process: segProcess, limiter: o.limiter, block: block, slug: slug, checkAbort: checkAbort,
                    onFrames: function (n) { doneFrames += n; onProgress(0.05 + 0.85 * Math.min(1, doneFrames / totalWork), 'encode'); } }));
            }
            var segs = await Promise.all(jobs);
            var firstChunk = true;
            for (var sj = 0; sj < segs.length; sj++) {
                if (segs[sj].maxGrDb > maxGrDb) maxGrDb = segs[sj].maxGrDb;
                for (var ci = 0; ci < segs[sj].chunks.length; ci++) {
                    var ck = segs[sj].chunks[ci];
                    muxer.addAudioChunk(new w.EncodedAudioChunk({ type: 'key', timestamp: ck.ts, duration: ck.dur, data: ck.data }), firstChunk ? segs[0].meta : undefined);
                    firstChunk = false; chunks++;
                }
            }
            parMs = Math.round(nowMs() - tP);
        }
        var framesFed = 0;
        var planar = new Float32Array(block * nCh);      // the block, channel planes back to back
        var packed = new Float32Array(block * nCh);      // limiter output re-packed the same way
        var steps = { copy: 0, process: 0, limit: 0, ad: 0, encode: 0, wait: 0, yield: 0 };   // where the block loop's time goes
        async function feedPlanar(buf, n) {
            var t1 = nowMs();
            var ad = new w.AudioData({
                format: 'f32-planar', sampleRate: procSr, numberOfChannels: nCh, numberOfFrames: n,
                timestamp: Math.round(framesFed / procSr * 1e6),
                data: buf.subarray(0, n * nCh)
            });
            var t2 = nowMs(); steps.ad += t2 - t1;
            try { encoder.encode(ad); } finally { ad.close(); }
            var t3 = nowMs(); steps.encode += t3 - t2;
            framesFed += n;
            if (encErr) throw encErr;
            // backpressure: the platform encoder runs off-thread; do not stack
            // a minute of AudioData in its queue
            while (encoder.encodeQueueSize > 8 && !encErr) await dequeueWait(encoder);
            steps.wait += nowMs() - t3;
        }
        async function feed(blocksOut) {
            for (var b = 0; b < blocksOut.length; b++) {
                var ch = blocksOut[b], n = ch[0].length, k;
                if (!n) continue;
                if (packed.length < n * nCh) packed = new Float32Array(n * nCh);
                for (k = 0; k < nCh; k++) packed.set(ch[k], k * n);
                await feedPlanar(packed, n);
            }
        }
        for (var off = 0; par < 2 && off < NV; off += block) {
            checkAbort();
            var n = Math.min(block, NV - off), views = [], s0 = nowMs();
            for (c = 0; c < nCh; c++) views.push(planar.subarray(c * n, (c + 1) * n));
            fillBlock(views, off, n);
            var s1 = nowMs(); steps.copy += s1 - s0;
            if (o.process && !enc.resample) o.process(views, off);
            var s2 = nowMs(); steps.process += s2 - s1;
            if (lim) { var lb = lim.push(views, n); steps.limit += nowMs() - s2; await feed(lb); }
            else await feedPlanar(planar, n);
            onProgress(0.05 + 0.85 * ((off + n) / NV), 'encode');
            var s3 = nowMs();
            await yieldMacro();
            steps.yield += nowMs() - s3;
        }
        if (par < 2) {
            if (lim) { var tail = lim.flush(); maxGrDb = tail.maxGrDb; await feed(tail.blocks); }
            checkAbort();
            await encoder.flush();
            try { encoder.close(); } catch (_) {}
        }
        if (encErr) throw laneErr('encoder-error', encErr && encErr.message);
        if (!chunks) throw laneErr('encoder-empty');
        muxer.finalize();
        var m4a = new File([muxer.target.buffer], 'soundtrack.m4a', { type: 'audio/mp4' });
        ms.encode = Math.round(nowMs() - tA);
        ms.flush = Math.round(nowMs() - tA - (steps.copy + steps.process + steps.limit + steps.ad + steps.encode + steps.wait + steps.yield));
        steps.output = outputMs;   // the encoder's output callbacks (muxer bookkeeping), which land in the gaps
        if (par >= 2) steps.par = parMs;
        for (var sk in steps) steps[sk] = Math.round(steps[sk]);
        ms.steps = steps;
        onProgress(0.92, 'remux');

        /* ── 2. Remux: zero-copy first, FFmpeg copy as the safety net ── */
        var tR = nowMs(), out, remux = 'zero-copy', remuxReason = '', remuxError = '', durable = false, flattened = false, quickTime = false, videoDelay = null;
        try {
            var r = await w.MP4TrackRemux.replaceAudio(o.file, m4a, { mime: o.file.type || 'video/mp4', maxParts: tune('remuxMaxParts', REMUX_MAX_PARTS) });
            out = r.blob; ms.remuxParts = r.parts; ms.remuxWaste = r.waste; durable = !!r.durable; flattened = !!r.flattened; quickTime = !!r.quickTime; videoDelay = r.videoDelay || null;
        } catch (re) {
            remuxReason = (re && re.remuxReason) || 'error';
            // the bare «error» class needs its message to be diagnosable in the field
            remuxError = String((re && re.message) || re || '').slice(0, 120);
            if (!(w.FFmpegProcessor && w.FFmpegProcessor.processMultiStreaming)) throw laneErr('remux:' + remuxReason, re && re.message);
            checkAbort();
            remux = 'ffmpeg';
            var outName = 'output.' + String(o.outExt || 'mp4').toLowerCase();
            /* An mp4 gets the picture's B-frame delay in its samples, as the
               zero-copy remux writes it (MP4TrackRemux «The picture's B-frame
               delay»: otherwise the sound plays early on Windows); a .mov keeps
               the edit list, as there. */
            var movflags = /\.(mp4|m4v)$/.test(outName) ? '+faststart+negative_cts_offsets' : '+faststart';
            var res = await w.FFmpegProcessor.processMultiStreaming([o.file, m4a],
                ['-map', '0:v:0', '-map', '1:a:0', '-c', 'copy', '-movflags', movflags], outName);
            out = res.blob;
        }
        ms.remux = Math.round(nowMs() - tR);
        ms.total = Math.round(nowMs() - t0);
        onProgress(1, 'done');
        return {
            blob: out, lane: 'native', remux: remux, remuxReason: remuxReason, remuxError: remuxError, durable: durable, flattened: flattened, quickTime: quickTime, videoDelay: videoDelay, ms: ms,
            audioSec: audioSec, par: par, startSec: L / procSr,
            encoder: { sampleRate: enc.sampleRate, bitrate: bitrate, resampled: !!enc.resample, chunks: chunks },
            maxGrDb: maxGrDb, audioBytes: m4a.size
        };
    }

    // window.<name> = … on purpose: the inline-identifier guard reads globals off that exact shape.
    window.VideoAudioLane = {
        probe: probe,
        run: run,
        warm: function () { return ensureDeps().catch(function () {}); },
        // For a caller that builds its own mix on the movie's clock: where the
        // video's decoded sound has to go in it (0 when unknown).
        soundStart: function (file) {
            return ensureDeps().then(function () { return w.MP4TrackRemux.soundStart(file); }).catch(function () { return 0; });
        },
        pickEncoderConfig: pickEncoderConfig,
        version: '1.0.0'
    };
})(window);
